import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, User } from 'lucide-react';
import { getBlogs } from '../services/api';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      const data = await getBlogs();
      setBlogs(data);
      setLoading(false);
    };
    fetchBlogs();
  }, []);

  return (
    <div className="bg-stone-50 min-h-screen pt-12 pb-24 border-t border-stone-200">
      <SEO
        title="Bareilly Spiritual Blogs & Nath Temple History | Nathnagri"
        description="Discover Bareilly’s spiritual blogs, Nath temple history, and devotional content. Learn about famous temples Bareilly, traditions, and travel tips."
        keywords="bareilly spiritual blogs, nath temple history bareilly, famous temples bareilly, hindu temple blogs india, nathnagri articles"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            <div className="bg-saffron-100 p-3 rounded-full">
              <BookOpen className="h-8 w-8 text-saffron-600" />
            </div>
          </div>
          <h1 className="text-4xl font-extrabold text-stone-900 tracking-tight mb-4">
            नवीनतम <span className="text-saffron-600">ब्लॉग और लेख</span>
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            नाथ नगरी के प्राचीन मंदिरों, धार्मिक आयोजनों और शहर के आध्यात्मिक इतिहास के बारे में हमारे रोचक लेख पढ़ें।
          </p>
        </div>

        {/* Blog Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white rounded-2xl h-[400px] border border-stone-100 shadow-sm animate-pulse flex flex-col">
                <div className="h-56 bg-stone-200 rounded-t-2xl"></div>
                <div className="p-5 flex-1 space-y-3">
                  <div className="h-6 bg-stone-200 rounded w-2/3"></div>
                  <div className="space-y-2 mt-4 text-sm text-stone-600">
                    <div className="h-4 bg-stone-200 rounded w-full"></div>
                    <div className="h-4 bg-stone-200 rounded w-5/6"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.map((blog, index) => (
              <Link 
                to={`/blog/${blog.slug || blog.id}`} 
                key={blog.id} 
                className="block group/link h-full border-none"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-stone-100 transition-all duration-300 flex flex-col h-full"
                >
                <div className="relative h-56 overflow-hidden bg-stone-100">
                  <OptimizedImage
                    src={blog.image}
                    alt={blog.title}
                    title={blog.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>

                <div className="p-6 flex-grow flex flex-col">
                  {/* Meta details */}
                  <div className="flex items-center space-x-4 text-xs font-medium text-stone-500 mb-3">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{blog.date}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <User className="w-3.5 h-3.5" />
                      <span>{blog.author}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="block mb-2">
                    <h2 className="text-xl font-bold text-stone-800 group-hover:text-saffron-600 transition-colors line-clamp-2">
                      {blog.title}
                    </h2>
                  </div>

                  {/* Excerpt */}
                  <p className="text-stone-600 text-sm leading-relaxed mb-6 line-clamp-3">
                    {blog.excerpt}
                  </p>

                  {/* Read More Button */}
                  <div className="mt-auto">
                    <span className="inline-flex items-center space-x-1 text-saffron-600 font-semibold text-sm group-hover/link:text-saffron-700 transition-colors">
                      <span>और पढ़ें</span>
                    </span>
                  </div>
                </div>
              </motion.div>
            </Link>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default Blog;
